import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { FC } from 'react';
import { NAVY, RADIUS_CARD, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import { PIAMONTE_BUSINESS_UNIT_ID, SALFA_SERVICIO_LIVIANO_BUSINESS_UNIT_ID } from 'utils/constants';

export interface MappedHeader {
  headerName: string;
  attrCliente: string;
}

export interface MapCsvHeadersProps {
  rows: { headerName: string; examples: string[] }[];
  mappedHeaders: MappedHeader[];
  setMappedHeaders: React.Dispatch<React.SetStateAction<MappedHeader[]>>;
  businessUnitType: 'car_dealership' | 'financial_institution' | 'insurance_broker' | 'after_sales';
  businessUnitId?: number;
}

const FONT_FAMILY = 'Jost, sans-serif';

const commonAttributes = {
  firstName: 'Nombre',
  lastName: 'Apellido',
  rut: 'Rut',
  email: 'Email',
  phone: 'Teléfono',
};

const insuranceBrokerAttributes = {
  ...commonAttributes,
  totalAskedAmount: 'Monto total solicitado',
  pendingBalance: 'Saldo pendiente',
  remainingQuotas: 'Cuotas restantes',
  institutionName: 'Nombre de la institución',
  loanProtectionInsuranceSimulationExternalId: 'ID simulación desgravamen',
  unemploymentInsuranceSimulationExternalId: 'ID simulación cesantía',
  loanProtectionRefundAmount: 'Reembolso desgravamen',
  unemploymentInsuranceRefundAmount: 'Reembolso cesantía',
};

// Vehicle the client already owns — persisted as a ClientCar row, matched by VIN or license plate.
const ownedCarAttributes = {
  ownedCarVin: 'VIN / N° de chasis',
  ownedCarLicensePlate: 'Patente del vehículo',
  ownedCarBrand: 'Marca del vehículo',
  ownedCarModel: 'Modelo del vehículo',
  ownedCarYear: 'Año del vehículo',
  ownedCarPurchaseDate: 'Fecha de compra del vehículo',
};

const afterSalesAttributes = {
  ...commonAttributes,
  branchOffice: 'Sucursal',
  externalId: 'ID externo',
  externalLeadOwner: 'Nombre ejecutivo',
  lastServiceDate: 'Última fecha de servicio',
  ...ownedCarAttributes,
};

const buildCarDealershipAttributes = (businessUnitId?: number) => ({
  ...commonAttributes,
  brandOfInterest: 'Marca de interés',
  model: 'Modelo',
  lastServiceDate: 'Última fecha de servicio',
  branchOffice: 'Sucursal',
  externalId: 'ID externo',
  externalLeadOwner: 'Nombre ejecutivo',
  ...(businessUnitId === SALFA_SERVICIO_LIVIANO_BUSINESS_UNIT_ID ? { lastKnownMileage: 'Kilometraje' } : {}),
  ...(businessUnitId === PIAMONTE_BUSINESS_UNIT_ID ? { executiveEmail: 'Email ejecutivo' } : {}),
});

export const getAttributesForBusinessType = (
  businessUnitType: MapCsvHeadersProps['businessUnitType'],
  businessUnitId?: number
): Record<string, string> => {
  switch (businessUnitType) {
    case 'car_dealership':
      return buildCarDealershipAttributes(businessUnitId);
    case 'financial_institution':
      return buildCarDealershipAttributes(businessUnitId);
    case 'insurance_broker':
      return insuranceBrokerAttributes;
    case 'after_sales':
      return afterSalesAttributes;
    default:
      return commonAttributes;
  }
};

const MapCsvHeaders: FC<MapCsvHeadersProps> = ({
  rows,
  mappedHeaders,
  setMappedHeaders,
  businessUnitType,
  businessUnitId,
}) => {
  const allAttributes = getAttributesForBusinessType(businessUnitType, businessUnitId);

  return (
    <Box>
      <Typography
        sx={{
          fontFamily: FONT_FAMILY,
          fontWeight: 600,
          color: NAVY,
          fontSize: '1.125rem',
          lineHeight: 1.3,
          mb: 2,
        }}
      >
        Mapear encabezados
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.4fr 1fr',
          gap: 2,
          mb: 1,
          px: 3,
        }}
      >
        {['Encabezado', 'Data', 'Atributo cliente'].map((label) => (
          <Typography
            key={label}
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              color: alpha(NAVY, 0.55),
              fontSize: '0.75rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {rows.map((row) => (
          <Box
            key={row.headerName}
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_CARD,
              boxShadow: SHADOW,
              p: 2.5,
              display: 'grid',
              gridTemplateColumns: '1fr 1.4fr 1fr',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Typography
              title={row.headerName}
              sx={{
                fontFamily: FONT_FAMILY,
                color: NAVY,
                fontSize: '0.95rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {row.headerName}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              {row.examples.map((example, idx) => (
                <Typography
                  key={`${row.headerName}-${idx}`}
                  title={example}
                  sx={{
                    fontFamily: FONT_FAMILY,
                    color: NAVY,
                    fontSize: '0.95rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {example}
                </Typography>
              ))}
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel id={`select-attr-${row.headerName}`}>Seleccionar atributo</InputLabel>
              <Select
                id={`select-attr-${row.headerName}`}
                value={mappedHeaders.find((header) => header.headerName === row.headerName)?.attrCliente || ''}
                label="Seleccionar atributo"
                onChange={(e) => {
                  const newValue = e.target.value;

                  if (newValue === '') {
                    setMappedHeaders((prev) => prev.filter((header) => header.headerName !== row.headerName));
                    return;
                  }
                  // Transfer the attribute to this header: drop any prior mapping for this header and
                  // remove the same attribute from any other header it was assigned to (incl. AI
                  // auto-assignments), so a manual selection always overrides and stays unique.
                  setMappedHeaders((prev) => [
                    ...prev.filter((header) => header.headerName !== row.headerName && header.attrCliente !== newValue),
                    { headerName: row.headerName, attrCliente: newValue },
                  ]);
                }}
              >
                <MenuItem value="">
                  <em>Ninguno</em>
                </MenuItem>
                {Object.entries(allAttributes).map(([key, value]) => {
                  // Greyed out as a hint when already mapped to another header — still selectable,
                  // selecting it transfers the attribute away from that header.
                  const usedElsewhere = mappedHeaders.some(
                    (header) => header.attrCliente === key && header.headerName !== row.headerName
                  );
                  return (
                    <MenuItem
                      value={key}
                      key={`${row.headerName}-${key}`}
                      sx={usedElsewhere ? { color: alpha(NAVY, 0.4) } : undefined}
                    >
                      {value}
                      {usedElsewhere ? ' · en uso' : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MapCsvHeaders;
